// Campaign module registry.
//
// A CampaignModule is a pre-authored adventure the GM can spin up a Nucleus game
// from. Picking a module in the Create Game wizard prefills the game details and
// the character-creation gating (which races / legacies / homeworlds are allowed).
//
// This registry is intentionally data-only and additive: to add another module,
// append an entry here (and, if it gates content, tag the relevant `properties`
// docs so the wizard can group them). Nothing else needs to change.

import type { Game } from '../models/game';
import {
	COLD_BOOTING_TAG,
	SOL_STANDARD_TAG,
	COLD_HOMEWORLD_IDS,
} from './coldBooting';

export interface CampaignModule {
	id: string;
	name: string;
	tagline: string;
	blurb: string;
	image: string;
	imagePrompt?: string; // prompt used to (re)generate the card/cover image
	route: string; // gmg page for this module
	order?: number; // display order in pickers

	// Prefill merged over defaultGame() when the module is chosen.
	gameDefaults: Partial<Game>;

	// Character-creation gating defaults.
	recommendedPlayerSlots: number;
	defaultAllowedRaces: string[]; // origin property ids ticked by default
	defaultAllowedLegacies: string[]; // legacy property ids ticked by default
	defaultAllowedHomeworlds: string[]; // homeWorld property ids ticked by default

	// Which tag-groups the wizard should show as ON by default. Groups not listed
	// here are shown but unticked, so the GM can re-enable a parent or its
	// individual children. `null` means "everything on" (the Other/custom case).
	enabledLegacyTags: string[] | null;
	enabledHomeworldTags: string[] | null;
}

const COLD_BOOTING: CampaignModule = {
	id: 'cold-booting',
	name: 'Cold Booting',
	tagline: 'The Ice Road — a desert haul that is alive, and thirsty.',
	blurb:
		'A mobile colony-train drinks the dying poles dry, dragging millions of litres of water ' +
		'along the Ice Road, the desert haul from Rimewell to Terminus Major, through a wasteland ' +
		'that is alive and thirsty. The starter campaign for Cybercore.',
	image: '/missions/cold-booting/cover.webp',
	imagePrompt:
		'A colossal nuclear-powered overland land-train hauling water tankers across a vast hostile ' +
		'cold desert at dusk, dieselpunk crag-city of Terminus Major on the horizon under a moonless ' +
		'sky, dust and exhaust, cel-shaded comic-book key art, muted ochres and cold blues, cinematic ' +
		'wide shot. Cybercore: Cold Booting cover.',
	route: '/gmg/modules/cold-booting',
	order: 1,

	gameDefaults: {
		name: 'Cold Booting',
		imageUrl: '/missions/cold-booting/cover.webp',
		description:
			'The Ice Road: haul the Iron Maiden and its cargo of water across a hostile desert from ' +
			'Rimewell to Terminus Major. Something out there is alive, and thirsty. Cybercore starter campaign.',
		system: 'Cybercore',
		table: 'Foundry VTT',
		frequency: 'Weekly',
		cost: 'Free',
		rank: 0,
		progression: 'Ranks 0-4',
		rpRatio: '60/40 Combat/RP',
		commitment: 'Weekly sessions, ~3 hours. Starter-friendly; new players welcome.',
		tags: ['Cold Booting', 'Campaign', 'Starter', 'Sci-Fi'],
	},

	recommendedPlayerSlots: 4,
	// Cold Booting allows only Human, Cyborg and Android by default.
	defaultAllowedRaces: ['origin-human-origin', 'origin-cyborg-origin', 'origin-android-origin'],
	// All legacies are allowed (empty = all); the cold-booting ones are called
	// out with a COLD BOOTING chip in the character editor instead of locking
	// the rest.
	defaultAllowedLegacies: [],
	defaultAllowedHomeworlds: COLD_HOMEWORLD_IDS,
	// Legacies unrestricted (null); homeworlds stay cold-booting only.
	enabledLegacyTags: null,
	enabledHomeworldTags: [COLD_BOOTING_TAG],
};

// The default Cybercore setting: the whole galaxy, all origins, the Sol / standard
// homeworlds and legacies. This is what "not running a specific module" means.
const CYBERCORE_STANDARD: CampaignModule = {
	id: 'cybercore-standard',
	name: 'Cybercore Standard',
	tagline: 'The default Cybercore setting — the whole galaxy, all origins.',
	blurb:
		'The baseline Cybercore universe. Every origin, the Sol / standard homeworlds, and the full ' +
		'standard legacy roster are available. Pick this when you are not running a specific module.',
	image: '/missions/standard/cover.webp',
		imagePrompt:
			'hyper-detailed cinematic sci-fi key art, painterly realism, dramatic volumetric lighting, no ' +
			'comic-book outlines :: a glossy portrait MAGAZINE / COMIC-BOOK COVER titled "CYBERCORE" :: the ' +
			'title "CYBERCORE" rendered large across the top as a bold chrome sci-fi logo, chunky angular ' +
			'tech-stencil lettering, brushed-metal with electric-blue edge glow, a small subtitle banner ' +
			'under it "The Outer Rim - All the Shitz" :: cover art below the logo: a ragtag away-team of SIX ' +
			'Cybercore origin races moving together toward the viewer down the corridor: a RODI ' +
			'@8WTH5GEjYck1tH0tr88w; the giant-ant @fHelYL8Ul5EHdeGLiKge slim exoskeleton walking on six ' +
			'legs, heavy mandibles, twitching antennae and a glowing Om translator headband, with a rotating ' +
			'minigun the size of the @fHelYL8Ul5EHdeGLiKge on its back; a @uPgA1EvVGMXLLwRPtByn, a large ' +
			'faceted crystalline biped with oversized arms and NO FACE, its silicon surface rippling like ' +
			'static, one arm reshaping seamlessly into a grown slug-thrower barrel; and a Junker ' +
			'@8oFt9Yy42yAu5jPXv6Pj, a floating sack of weapons and parts; a @VliH2Zwq7jaP3MMjf5mL carrying a ' +
			'large assault rifle and @J4Y9eUwCMjfrQzxTk8Lb carrying a rocket launcher :: they advance down a ' +
			'long, very dark alien corridor whose walls are encrusted with wet biomechanical growth and ' +
			'alien filth, almost no light except a few failing amber emergency strips and drifting mist, ' +
			'most of the corridor swallowed in deep blackness :: behind them, a menacing alien predator seen ' +
			'only as a huge dark SILHOUETTE looming in the gloom, a backlit black shape, its scorpion-like ' +
			'quadruped body and long segmented tail arched overhead barely readable in the dark, forelimbs ' +
			'like blades, only its glowing red eyes burning through the blackness, ominous, sinister :: ' +
			'mood: horror, very dark, poorly lit, oppressive shadow, menacing, dread, tension, cinematic :: ' +
			'PRESENTATION: the printed cover FILLS THE FRAME in portrait orientation, viewed nearly face-on ' +
			'from only a slight high angle (a straight magazine product shot), glossy magazine paper lying ' +
			'flat on a clear glass rectangle with a page curl at the bottom corner, crisp specular glints ' +
			'and soft reflections, only a thin border of dark glass around the edges :: extremely dark ' +
			'low-key lighting, near-black, murky, most of the frame in deep shadow, only faint sickly ' +
			'rim-light and the glowing red eyes, high contrast, ultra-detailed, highres, professional ' +
			'photoreal cinematic concept art --ar 2:3 --style raw',
	route: '/gmg/modules/cybercore-standard',
	order: 2,

	gameDefaults: {
		system: 'Cybercore',
		description: 'A standard Cybercore campaign.',
		tags: ['Cybercore', 'Standard'],
	},

	recommendedPlayerSlots: 4,
	defaultAllowedRaces: [], // empty = all origins allowed
	defaultAllowedLegacies: [], // resolved to all sol-standard legacies by the consumer
	defaultAllowedHomeworlds: [],
	enabledLegacyTags: [SOL_STANDARD_TAG],
	enabledHomeworldTags: [SOL_STANDARD_TAG],
};

export const CAMPAIGN_MODULES: CampaignModule[] = [COLD_BOOTING, CYBERCORE_STANDARD];

// The "Other" pseudo-module: a custom game with no restrictions (everything on).
export const CUSTOM_MODULE: CampaignModule = {
	id: '',
	name: 'Other / Custom',
	tagline: 'A game of your own, no restrictions.',
	blurb: 'Start from a blank slate. All races, legacies and homeworlds are available.',
	image: '/missions/custom/cover.webp',
	route: '',
	gameDefaults: { imageUrl: '/missions/custom/cover.webp' },
	recommendedPlayerSlots: 4,
	defaultAllowedRaces: [], // empty = all allowed
	defaultAllowedLegacies: [],
	defaultAllowedHomeworlds: [],
	enabledLegacyTags: null, // null = everything on
	enabledHomeworldTags: null,
};

export function getModule(id: string | undefined | null): CampaignModule {
	if (!id) return CUSTOM_MODULE;
	return CAMPAIGN_MODULES.find((m) => m.id === id) ?? CUSTOM_MODULE;
}

// Human-friendly labels for the tag groups the wizard renders.
export const TAG_GROUP_LABELS: Record<string, string> = {
	[COLD_BOOTING_TAG]: 'Cold Booting',
	[SOL_STANDARD_TAG]: 'Sol / Standard',
	other: 'Other',
};

export { COLD_BOOTING_TAG, SOL_STANDARD_TAG };
